import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { Turnstile } from "./Turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export const ContactForm = ({ compact = false }) => {
  const c = useContent("contact");
  const [form, setForm] = useState({
    nom: "",
    entreprise: "",
    telephone: "",
    email: "",
    objet: "",
    message: "",
    website: "", // honeypot
  });
  const [sending, setSending] = useState(false);
  const [openedAt] = useState(() => Date.now());
  const [turnstileToken, setTurnstileToken] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.objet || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Merci de patienter quelques secondes — la vérification anti-spam se charge.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        ...form,
        form_ts: Date.now() - openedAt,
        turnstile_token: turnstileToken,
      });
      toast.success(c.form_success_message || "Votre message a bien été envoyé.");
      setForm({ nom: "", entreprise: "", telephone: "", email: "", objet: "", message: "", website: "" });
      setTurnstileToken("");
      // Reset turnstile widget so a new token is generated on next submit
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch (e) { /* noop */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-gray-300 focus:border-[#E60000] focus:outline-none focus:ring-0 px-0 py-3 text-base placeholder:text-gray-400 transition-colors";

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className={`bg-white ${compact ? "p-8 lg:p-10" : "p-10 lg:p-14"} border border-gray-200`}>
      {/* Honeypot field — invisible to humans, bots fill it */}
      <div aria-hidden="true" className="absolute opacity-0 pointer-events-none -left-[9999px] h-0 overflow-hidden">
        <label htmlFor="cf-website">Site web (ne pas remplir)</label>
        <input
          tabIndex="-1"
          autoComplete="off"
          id="cf-website"
          name="website"
          type="text"
          value={form.website}
          onChange={onChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="overline block mb-1" htmlFor="cf-nom">Nom *</label>
          <input data-testid="contact-input-nom" id="cf-nom" name="nom" value={form.nom} onChange={onChange} required className={inputClass} placeholder="Votre nom" />
        </div>
        <div>
          <label className="overline block mb-1" htmlFor="cf-entreprise">Entreprise</label>
          <input data-testid="contact-input-entreprise" id="cf-entreprise" name="entreprise" value={form.entreprise} onChange={onChange} className={inputClass} placeholder="Société / Organisme" />
        </div>
        <div>
          <label className="overline block mb-1" htmlFor="cf-tel">Téléphone</label>
          <input data-testid="contact-input-tel" id="cf-tel" name="telephone" value={form.telephone} onChange={onChange} className={inputClass} placeholder="+33 …" />
        </div>
        <div>
          <label className="overline block mb-1" htmlFor="cf-email">E-mail *</label>
          <input data-testid="contact-input-email" id="cf-email" name="email" type="email" value={form.email} onChange={onChange} required className={inputClass} placeholder="vous@exemple.fr" />
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-1" htmlFor="cf-objet">Objet *</label>
          <input data-testid="contact-input-objet" id="cf-objet" name="objet" value={form.objet} onChange={onChange} required className={inputClass} placeholder="Type de projet, besoin d'accompagnement…" />
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-1" htmlFor="cf-msg">Message *</label>
          <textarea data-testid="contact-input-message" id="cf-msg" name="message" value={form.message} onChange={onChange} required rows={5} className={inputClass + " resize-none"} placeholder="Décrivez votre projet en quelques lignes…" />
        </div>
      </div>
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col gap-3">
          {TURNSTILE_SITE_KEY && (
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={(t) => setTurnstileToken(t)}
              onExpire={() => setTurnstileToken("")}
              onError={() => setTurnstileToken("")}
            />
          )}
          <p className="text-xs text-gray-500">* champs obligatoires — vos données restent confidentielles.</p>
        </div>
        <button
          type="submit"
          data-testid="contact-submit-btn"
          disabled={sending || (TURNSTILE_SITE_KEY && !turnstileToken)}
          className="inline-flex items-center justify-center bg-[#E60000] hover:bg-[#B30000] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-sm font-medium tracking-wide uppercase transition-colors"
        >
          {sending ? "Envoi en cours…" : (c.form_submit_label || "Envoyer le message")}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
